import React from 'react';
import {Column} from '@ant-design/plots';
import {Card, Row, Col, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {NoDataPlaceholder, truncateStr} from '../../../utils/helpers';

type CLicksRateProps = {
    promotersPerClickRate: any,
    clicksPerClickRate: any,
    clicksPerCampaignBudget: any
};

interface DataItem {
    clickRate: string;
    value: number;
    type: string;
}

interface clicksPerCampaignBudget {
    campaign_budget: string;
    numberOfClicks: number;
}

const FONT = 'Poppins';

const ClicksRateGraph = ({clicksPerClickRate, promotersPerClickRate, clicksPerCampaignBudget}: CLicksRateProps) => {

    clicksPerClickRate = clicksPerClickRate.map(({numberOfClicks, ...rest}: any) => ({
        value: numberOfClicks,
        type: 'Clicks',
        ...rest
    }));

    promotersPerClickRate = promotersPerClickRate.map(({numberOfPromoters, ...rest}: any) => ({
        value: numberOfPromoters ?? 0,
        type: 'Promoters',
        ...rest
    }));

    const clicksPerPromoters: DataItem[] = clicksPerClickRate.concat(promotersPerClickRate)
    const clicksPerBudget: clicksPerCampaignBudget[] = clicksPerCampaignBudget
        .map(({numberOfClicks, ...rest}: any) => ({'Clicks': numberOfClicks, ...rest}));

    const configBudget = {
        data: clicksPerBudget || [],
        xField: 'campaign_budget',
        yField: 'Clicks',
        xAxis: {label: {autoHide: true, autoRotate: false}},
        shapeField: 'column25D',
        style: {fill: 'rgba(126, 212, 236, 0.8)'},
        responsive: true,
        height: 320,
        autoFit: true,
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true,
                labelRotate: -0.6,
                formatter: (v: string) => truncateStr(v, 10)
            },
            y: {labelFontFamily: FONT},
        },
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        }
    };

    const config = {
        data: clicksPerPromoters || [],
        xField: 'clickRate',
        yField: 'value',
        colorField: 'type',
        isGroup: true,
        style: {inset: 5},
        responsive: true,
        height: 320,
        autoFit: true,
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true,
                labelRotate: -0.6,
                formatter: (v: string) => truncateStr(v, 10)
            },
            y: {labelFontFamily: FONT},
        },
        tooltip: {
            title: {style: {fontFamily: FONT}},
            itemName: {style: {fontFamily: FONT}},
            itemValue: {style: {fontFamily: FONT}}
        }
    };

    const ClicksPerBudgetCustomizedCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Number of clicks per campaign budget</span>
            <Tooltip title="Number of clicks gained for different campaign budgets">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    const ClickRateCustomizedCardTitle = () => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Number of clicks per click rate and number of promoters per click rate</span>
            <Tooltip title="Number of clicks gained for different click rate prices">
                <InfoCircleOutlined/>
            </Tooltip>
        </div>
    );

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                    <Card title={<ClicksPerBudgetCustomizedCardTitle/>}>
                        {clicksPerBudget.length > 0 ? (
                            <Column {...configBudget} />
                        ) : (
                            <NoDataPlaceholder message="No click budget data to display"/>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                    <Card title={<ClickRateCustomizedCardTitle/>}>
                        {clicksPerPromoters.length > 0 ? (
                            <Column {...config} />
                        ) : (
                            <NoDataPlaceholder message="No click promoter data to display"/>
                        )}
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default ClicksRateGraph;